import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { PrinterIcon } from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ReportTextCell, CrewDataTableFrame, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import {
  formatStatNum,
  StatsBucketChart,
  StatsBucketHeader,
  StatsCountCell,
  StatsKpiListLayout,
  StatsMixPanel,
  StatsPageShell,
  StatsRankedBars,
} from './statisticsShared';
import { availableCountPrintPath } from './reports/availableCountPrintMeta';
import type { CrewStatMixItem, CrewStatsAvailableRow } from './types';

/** Calendar months from `fromIso` (today) until `availableFrom`. */
function monthsUntil(availableFrom: string, asOf: Date): number {
  const b = new Date(`${availableFrom}T00:00:00.000Z`);
  let months = (b.getUTCFullYear() - asOf.getUTCFullYear()) * 12 + (b.getUTCMonth() - asOf.getUTCMonth());
  if (b.getUTCDate() < asOf.getUTCDate()) months -= 1;
  return months;
}

function utcToday(): Date {
  const n = new Date();
  return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()));
}

export const AVAIL_BUCKETS = [
  { key: 'now', label: 'Now' },
  { key: 'm0_1', label: '< 1 mo' },
  { key: 'm1_3', label: '1–3 mo' },
  { key: 'm3_6', label: '3–6 mo' },
  { key: 'm6', label: '≥ 6 mo' },
] as const;

const AVAIL_BAR: Record<string, string> = {
  now: 'bg-sky-500',
  m0_1: 'bg-blue-500',
  m1_3: 'bg-indigo-500',
  m3_6: 'bg-amber-500',
  m6: 'bg-rose-500',
  unknown: 'bg-slate-400',
};

const AVAIL_BAR_BY_LABEL: Record<string, string> = {
  Now: AVAIL_BAR.now,
  '< 1 mo': AVAIL_BAR.m0_1,
  '1–3 mo': AVAIL_BAR.m1_3,
  '3–6 mo': AVAIL_BAR.m3_6,
  '≥ 6 mo': AVAIL_BAR.m6,
  'No date': AVAIL_BAR.unknown,
};

type AvailBucketKey = (typeof AVAIL_BUCKETS)[number]['key'] | 'unknown';

export type AvailableCountRow = {
  key: string;
  lastVessel: string;
  nationality: string;
  now: number;
  m0_1: number;
  m1_3: number;
  m3_6: number;
  m6: number;
  unknown: number;
  total: number;
};

function availBucket(availableFrom: string | null | undefined, asOf = utcToday()): AvailBucketKey {
  if (!availableFrom) return 'unknown';
  const from = new Date(`${availableFrom}T00:00:00.000Z`);
  if (from.getTime() <= asOf.getTime()) return 'now';
  const months = monthsUntil(availableFrom, asOf);
  if (months < 1) return 'm0_1';
  if (months < 3) return 'm1_3';
  if (months < 6) return 'm3_6';
  return 'm6';
}

export function buildAvailableCountRows(available: CrewStatsAvailableRow[]): AvailableCountRow[] {
  const map = new Map<string, AvailableCountRow>();
  for (const row of available) {
    const lastVessel = row.lastVessel?.trim() || '—';
    const nationality = row.nationality?.trim() || 'Unknown';
    const key = `${lastVessel}\0${nationality}`;
    let agg = map.get(key);
    if (!agg) {
      agg = {
        key,
        lastVessel,
        nationality,
        now: 0,
        m0_1: 0,
        m1_3: 0,
        m3_6: 0,
        m6: 0,
        unknown: 0,
        total: 0,
      };
      map.set(key, agg);
    }
    agg[availBucket(row.availableFrom)] += 1;
    agg.total += 1;
  }
  return Array.from(map.values()).sort((a, b) => {
    const byVessel = a.lastVessel.localeCompare(b.lastVessel, undefined, { sensitivity: 'base' });
    if (byVessel !== 0) return byVessel;
    return a.nationality.localeCompare(b.nationality, undefined, { sensitivity: 'base' });
  });
}

export function availMixFromCountRows(rows: AvailableCountRow[]): CrewStatMixItem[] {
  const items: CrewStatMixItem[] = AVAIL_BUCKETS.map((b) => ({
    label: b.label,
    count: rows.reduce((s, r) => s + r[b.key], 0),
  }));
  const unknown = rows.reduce((s, r) => s + r.unknown, 0);
  if (unknown > 0) items.push({ label: 'No date', count: unknown });
  return items;
}

export function nationalityMixFromAvailableRows(rows: AvailableCountRow[]): CrewStatMixItem[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    map.set(row.nationality, (map.get(row.nationality) ?? 0) + row.total);
  }
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

const AvailableMixPanel: React.FC<{
  availableCount: number;
  vesselCount: number;
  nationalityCount: number;
  availMix: CrewStatMixItem[];
  nationalityMix: CrewStatMixItem[];
}> = ({ availableCount, vesselCount, nationalityCount, availMix, nationalityMix }) => {
  const { colors } = useTheme();
  return (
    <StatsMixPanel
      metrics={[
        { label: 'Available', value: formatStatNum(availableCount) },
        { label: 'Last vessels', value: formatStatNum(vesselCount) },
        { label: 'Nationalities', value: formatStatNum(nationalityCount) },
      ]}
    >
      <StatsBucketChart
        title="Available from"
        hint="Until available"
        data={availMix}
        total={availableCount}
        emptyLabel="No available crew"
        barClassFor={(label) => AVAIL_BAR_BY_LABEL[label] ?? AVAIL_BAR.unknown}
      />
      <div className={cn('shrink-0 border-t', colors.border)} />
      <StatsRankedBars
        title="Nationality mix"
        hint={
          nationalityMix.length
            ? `${nationalityMix.length} ${nationalityMix.length === 1 ? 'nationality' : 'nationalities'}`
            : undefined
        }
        data={nationalityMix}
        total={availableCount}
        emptyLabel="No available crew"
      />
    </StatsMixPanel>
  );
};

const AvailableStatisticsPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const [listTab, setListTab] = useState<'summary' | 'crew'>('summary');

  const summaryColumns: Column<AvailableCountRow>[] = useMemo(() => {
    const bucketCols: Column<AvailableCountRow>[] = AVAIL_BUCKETS.map((b) => ({
      key: b.key,
      header: <StatsBucketHeader colorClass={AVAIL_BAR[b.key]} label={b.label} />,
      sortable: true,
      align: 'right' as const,
      footerType: 'sum' as const,
      columnGroup: { id: 'avail', header: 'Available from' },
      render: (v: unknown) => <StatsCountCell value={v} emphasize={b.key === 'm6'} />,
    }));
    return [
      {
        key: 'lastVessel',
        header: 'Last vessel',
        sortable: true,
        render: (v: string) => <ReportTextCell value={v} />,
      },
      {
        key: 'nationality',
        header: 'Nationality',
        sortable: true,
        render: (v: string) => <ReportTextCell value={v} />,
      },
      ...bucketCols,
      {
        key: 'unknown',
        header: <StatsBucketHeader colorClass={AVAIL_BAR.unknown} label="No date" />,
        sortable: true,
        align: 'right',
        footerType: 'sum',
        columnGroup: { id: 'avail', header: 'Available from' },
        render: (v: unknown) => <StatsCountCell value={v} />,
      },
      {
        key: 'total',
        header: 'Total',
        sortable: true,
        align: 'right',
        footerType: 'sum',
        render: (v: number) => (
          <span className={cn('tabular-nums font-semibold', colors.text)}>{v}</span>
        ),
      },
    ];
  }, [colors.text]);

  const crewColumns: Column<CrewStatsAvailableRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Crew',
        sortable: true,
        render: (_v, row) => (
          <Link
            to={`/crew/${row.crewId}`}
            state={{ from: listReturnPath }}
            className={cn('text-sm hover:underline', colors.primaryFg)}
            onClick={(e) => e.stopPropagation()}
          >
            {row.name}
          </Link>
        ),
      },
      { key: 'rank', header: 'Rank', sortable: true, render: (v: string | null) => <ReportTextCell value={v} /> },
      {
        key: 'nationality',
        header: 'Nationality',
        sortable: true,
        render: (v: string | null) => <ReportTextCell value={v} />,
      },
      {
        key: 'availableFrom',
        header: 'Av. from',
        sortable: true,
        render: (v: string | null) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'availableTo',
        header: 'Av. to',
        sortable: true,
        render: (v: string | null) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'availabilityStatus',
        header: 'Av. status',
        sortable: true,
        render: (v: string | null) => <ReportTextCell value={v} />,
      },
      {
        key: 'lastVessel',
        header: 'Last vessel',
        sortable: true,
        render: (v: string | null) => <ReportTextCell value={v} />,
      },
      { key: 'lastRank', header: 'Last rank', sortable: true, render: (v: string | null) => <ReportTextCell value={v} /> },
    ],
    [listReturnPath, colors.primaryFg],
  );

  return (
    <StatsPageShell
      hintTitle="Available"
      hintBody="Count of relief-pool crew per last vessel, nationality, and months until available from (excluding not-recommended)."
      filterMode="vessel"
      trailingActions={({ applied }) =>
        applied ? (
          <Link
            to={availableCountPrintPath({ vesselId: applied.vesselId })}
            title="Print preview"
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
              colors.border,
              colors.text,
              'hover:bg-black/5 dark:hover:bg-white/10',
            )}
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </Link>
        ) : null
      }
    >
      {({ data }) => {
        const available = data.lists?.available ?? [];
        const rows = buildAvailableCountRows(available);
        const vesselCount = new Set(rows.map((r) => r.lastVessel)).size;
        const nationalityCount = new Set(rows.map((r) => r.nationality)).size;
        const showUnknown = rows.some((r) => r.unknown > 0);
        const columns = showUnknown
          ? summaryColumns
          : summaryColumns.filter((c) => c.key !== 'unknown');
        const availMix = availMixFromCountRows(rows);
        const nationalityMix = nationalityMixFromAvailableRows(rows);

        return (
          <StatsKpiListLayout
            charts={
              <AvailableMixPanel
                availableCount={available.length}
                vesselCount={vesselCount}
                nationalityCount={nationalityCount}
                availMix={availMix}
                nationalityMix={nationalityMix}
              />
            }
            chartsPlacement="right"
            tabs={[
              { id: 'summary', label: 'Counts', count: rows.length },
              { id: 'crew', label: 'Crew', count: available.length },
            ]}
            activeTab={listTab}
            onTabChange={(id) => setListTab(id === 'crew' ? 'crew' : 'summary')}
          >
            <CrewDataTableFrame>
              {listTab === 'summary' ? (
                <DataTable
                  data={rows}
                  columns={columns}
                  keyExtractor={(row) => row.key}
                  emptyMessage="No available crew"
                  showFooter={rows.length > 0}
                />
              ) : (
                <DataTable
                  data={available}
                  columns={crewColumns}
                  keyExtractor={(row) => row.crewId}
                  emptyMessage="No available crew"
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              )}
            </CrewDataTableFrame>
          </StatsKpiListLayout>
        );
      }}
    </StatsPageShell>
  );
};

export default AvailableStatisticsPage;
